import { Injectable, inject, signal, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from '@angular/fire/auth';
import { from, of, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TenantService } from '../services/tenant.service';

export const ON_AUTH_STATE_CHANGED = new InjectionToken<typeof onAuthStateChanged>(
  'onAuthStateChanged',
  { factory: () => onAuthStateChanged }
);

const MOCK_USER = { uid: 'mock-operator', email: 'operator@omnita.com', displayName: 'Operator Admin', getIdToken: async () => 'mock-token' } as unknown as User;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private router = inject(Router);
  private tenantService = inject(TenantService);
  private onAuthStateChanged = inject(ON_AUTH_STATE_CHANGED);

  readonly currentUser = signal<User | null>(null);
  readonly isLoading = signal(true);

  constructor() {
    if (environment.skipFirebaseAuth) {
      this.currentUser.set(MOCK_USER);
      this.isLoading.set(false);
    } else {
      this.onAuthStateChanged(this.auth, (user) => {
        this.currentUser.set(user);
        this.isLoading.set(false);
      });
    }
  }

  async getIdToken(): Promise<string | null> {
    if (environment.skipFirebaseAuth) return 'mock-token';
    const user = this.auth.currentUser ?? this.currentUser();
    if (!user) return null;
    return user.getIdToken();
  }

  /** Wait for Firebase auth bootstrap before attaching tokens to GraphQL requests. */
  waitForAuthReady(timeout = 5000): Promise<User | null> {
    if (environment.skipFirebaseAuth) return Promise.resolve(MOCK_USER);

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Auth loading timed out')), timeout);

      const check = () => {
        const user = this.auth.currentUser ?? this.currentUser();
        if (user) {
          clearTimeout(timer);
          resolve(user);
          return;
        }
        if (!this.isLoading()) {
          clearTimeout(timer);
          resolve(null);
          return;
        }
        setTimeout(check, 50);
      };

      check();
    });
  }

  login(email: string, password: string): Observable<void> {
    if (environment.skipFirebaseAuth) return of(undefined);
    return from(
      signInWithEmailAndPassword(this.auth, email, password).then(() => {})
    );
  }

  async logout(): Promise<void> {
    if (!environment.skipFirebaseAuth) await signOut(this.auth);
    this.currentUser.set(null);
    this.isLoading.set(false);
    this.tenantService.reset();
    await this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return !!this.currentUser();
  }
}
